/**
 * Ask — the app's ONLY dialog: a themed overlay with positive choices that
 * replaces every window.alert / prompt / confirm. Promise-based imperative
 * API so call sites stay one-liners:
 *   await ask({ title, message, ok: 'OK' })                      → notice
 *   if (await ask({ title, message, ok: 'Remove', danger }))     → confirm
 *   const v = await ask({ title, message, input: true, value })  → prompt
 * Input mode resolves the string (null on cancel); other modes resolve a
 * boolean. AskHost renders once in App. No backdrop-close — choices only.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';

let listener = null;

export const ask = (opts) => new Promise((resolve) => {
  if (listener) listener({ ...opts, resolve });
  else resolve(opts.input ? null : false);   // host not mounted — fail safe
});

export function AskHost() {
  const [q, setQ] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    listener = setQ;
    return () => { listener = null; };
  }, []);

  useEffect(() => {
    if (q?.input) inputRef.current?.focus();
  }, [q]);

  if (!q) return null;

  const finish = (val) => { q.resolve(val); setQ(null); };
  const okay = () => finish(q.input ? (inputRef.current?.value ?? '') : true);

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center z-[90] px-6">
      <div className="card max-w-sm w-full shadow-2xl">
        <h3 className="font-bold">{q.title || 'Just checking'}</h3>
        {q.message && (
          <p className="text-sm text-slate-400 mt-2 whitespace-pre-line">{q.message}</p>
        )}
        {q.input && (
          <input ref={inputRef} className="input mt-3 text-right font-medium"
                 defaultValue={q.value ?? ''}
                 onKeyDown={(e) => { if (e.key === 'Enter') okay();
                                     if (e.key === 'Escape') finish(null); }} />
        )}
        <div className="mt-4 flex gap-3">
          <button className={`flex-1 rounded-xl py-2.5 text-sm transition ${
                    q.danger
                      ? 'border border-red-400/60 text-red-400 hover:bg-red-400/10'
                      : 'btn-primary'}`}
                  onClick={okay}>
            {q.ok || 'OK'}
          </button>
          {q.alt && (
            <button className="flex-1 border border-vblue/50 rounded-xl py-2.5 text-sm text-vblue hover:bg-vblue/10"
                    onClick={() => finish('alt')}>
              {q.alt}
            </button>
          )}
          {(q.cancel !== false) && (q.input || q.cancel || q.ok !== 'OK') && (
            <button className="flex-1 border border-edge rounded-xl py-2.5 text-sm text-slate-400"
                    onClick={() => finish(q.input ? null : false)}>
              {typeof q.cancel === 'string' ? q.cancel : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
